/**
 * ErrorBoundary.jsx — Catches render errors from lazy chunks / route
 * components so we never ship a "white screen of death" to users.
 *
 * Two failure modes we protect against:
 *   1. React render error inside a lazy chunk → shows a fallback with
 *      a "Recharger la page" CTA.
 *   2. `ChunkLoadError` from webpack (network hiccup, stale cache after
 *      deploy) → triggers a HARD reload (`location.reload()`) so the
 *      browser fetches the new manifest and downloads fresh chunks.
 *
 * Audit U-1 (2026-07-06) — fixes the "no error boundary around lazy
 * routes" P1 finding.
 */
import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // ChunkLoadError → user has stale manifest after a deploy. Reload once,
    // guarded by sessionStorage so we don't loop if the chunk really is
    // gone from the CDN.
    const msg = String(error?.name || '') + ' ' + String(error?.message || '');
    if (/ChunkLoadError|Loading chunk|Failed to fetch dynamically imported/i.test(msg)) {
      try {
        if (!sessionStorage.getItem('r237_chunk_reload')) {
          sessionStorage.setItem('r237_chunk_reload', '1');
          window.location.reload();
          return;
        }
      } catch { /* sessionStorage may be blocked */ }
    }
    console.error('[ErrorBoundary]', error, info?.componentStack);
    // Ship to PostHog if available (safe optional chaining).
    try {
      if (window?.posthog?.capture) {
        window.posthog.capture('$exception', {
          message: error?.message,
          stack: error?.stack,
          component_stack: info?.componentStack,
        });
      }
    } catch { /* posthog optional */ }
  }

  reset = () => {
    try { sessionStorage.removeItem('r237_chunk_reload'); } catch { /* noop */ }
    this.setState({ hasError: false, error: null });
  };

  reload = () => {
    try { sessionStorage.removeItem('r237_chunk_reload'); } catch { /* noop */ }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const msg = String(this.state.error?.message || '');
      const isNetwork = /network|fetch|failed to load|chunk/i.test(msg);
      return (
        <section
          className="min-h-[70vh] bg-paper flex items-center justify-center px-4 py-16"
          data-testid="error-boundary-fallback"
          role="alert"
        >
          <div className="max-w-2xl w-full text-center">
            {/* Animated brand logo */}
            <div className="flex justify-center mb-6">
              <img
                src="/logo-animated.svg"
                alt="Realty237"
                width="160"
                height="160"
                className="h-36 w-36 sm:h-40 sm:w-40 rounded-3xl shadow-xl"
                draggable="false"
              />
            </div>

            <span
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold tracking-wider uppercase"
              data-testid="err-badge"
            >
              {isNetwork ? 'Erreur de connexion' : 'Erreur inattendue'}
            </span>

            <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 mt-4">
              {isNetwork ? 'Connexion interrompue' : 'Une erreur est survenue'}
            </h1>

            <p className="mt-4 text-stone-600 leading-relaxed max-w-xl mx-auto">
              {isNetwork
                ? "Impossible de joindre nos serveurs. Vérifiez votre connexion internet, puis réessayez."
                : "Nous n'avons pas pu afficher cette page. Rechargez pour réessayer — vos données ne sont pas perdues."}
            </p>

            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <details className="mt-4 text-left text-xs bg-stone-100 border border-stone-200 rounded-lg p-3 max-w-lg mx-auto">
                <summary className="cursor-pointer font-mono text-stone-700 select-none">
                  Détails techniques (dev)
                </summary>
                <pre className="mt-2 text-stone-600 whitespace-pre-wrap break-words font-mono">
                  {String(this.state.error?.message || this.state.error)}
                </pre>
              </details>
            )}

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={this.reload}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition shadow-sm"
                data-testid="error-boundary-reload"
              >
                Recharger la page
              </button>
              <button
                onClick={this.reset}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg border border-stone-300 text-stone-700 hover:bg-stone-50 text-sm font-medium transition"
                data-testid="error-boundary-retry"
              >
                Réessayer
              </button>
              <a
                href="/"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-stone-600 hover:text-orange-600 text-sm font-medium transition"
                data-testid="error-boundary-home"
              >
                Retour à l&apos;accueil
              </a>
            </div>

            <p className="mt-6 text-xs text-stone-500">
              Le problème persiste ? Écrivez-nous à{' '}
              <a href="mailto:support@realty237.com" className="text-orange-600 hover:underline">
                support@realty237.com
              </a>
            </p>
          </div>
        </section>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
